import { useEffect, useState } from 'react';
import { Modal, Input, Select, Alert, message } from 'antd';
import * as claus from 'src/_shared/services/claus/endpoints';

// Create an SFTP credential. The create response contains ONE-TIME secrets (password OR key pair)
// that must be captured before dismissing.
export default function SftpCredentialsDialog({ orgId, open, onClose, onCreated }) {
	const [name, setName] = useState('');
	const [authType, setAuthType] = useState('sftp_userpass');
	const [saving, setSaving] = useState(false);
	const [result, setResult] = useState(null);

	useEffect(() => {
		if (open) {
			setName('');
			setAuthType('sftp_userpass');
			setResult(null);
		}
	}, [open]);

	async function create() {
		if (!name.trim()) {
			message.error('Name is required.');
			return;
		}
		setSaving(true);
		try {
			const res = await claus.createSftpCredential(orgId, { name, authType });
			setResult(res);
		} catch (e) {
			message.error(e.message || 'Could not create the credential.');
		} finally {
			setSaving(false);
		}
	}

	const creds = result?.credentials || {};

	return (
		<Modal
			title="Add SFTP credential"
			open={open}
			onCancel={() => {
				if (result) {
					// the secrets on screen are one-time — dismissing via X must be deliberate
					Modal.confirm({
						title: 'Close without saving the credentials?',
						content: 'These secrets are shown only once and cannot be retrieved later.',
						okText: 'I saved them — close',
						cancelText: 'Keep open',
						okButtonProps: { danger: true },
						onOk: onCreated,
					});
				} else {
					onClose();
				}
			}}
			onOk={result ? onCreated : create}
			okText={result ? 'Done' : 'Create'}
			confirmLoading={saving}
			wrapClassName="erin-integrations"
		>
			{!result ? (
				<>
					<div className="ei-field">
						<label className="ei-field-label">Name</label>
						<Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme SFTP" />
					</div>
					<div className="ei-field">
						<label className="ei-field-label">Auth type</label>
						<Select
							value={authType}
							onChange={setAuthType}
							style={{ width: '100%' }}
							options={[
								{ value: 'sftp_userpass', label: 'Username / password' },
								{ value: 'sftp_ssh', label: 'SSH key' },
							]}
						/>
					</div>
				</>
			) : (
				<>
					<Alert
						type="warning"
						showIcon
						message="Save these credentials now"
						description="They are shown only once and cannot be retrieved later."
						style={{ marginBottom: 12 }}
					/>
					<div className="ei-field">
						<label className="ei-field-label">Username</label>
						<Input readOnly value={result.username} />
					</div>
					{creds.password && (
						<div className="ei-field">
							<label className="ei-field-label">Password</label>
							<Input.TextArea readOnly value={creds.password} autoSize />
						</div>
					)}
					{creds.privateKey && (
						<div className="ei-field">
							<label className="ei-field-label">Private key</label>
							<Input.TextArea readOnly value={creds.privateKey} autoSize={{ minRows: 3, maxRows: 8 }} className="js-mono" />
						</div>
					)}
					{creds.publicKey && (
						<div className="ei-field">
							<label className="ei-field-label">Public key</label>
							<Input.TextArea readOnly value={creds.publicKey} autoSize={{ minRows: 2, maxRows: 6 }} className="js-mono" />
						</div>
					)}
				</>
			)}
		</Modal>
	);
}
